import {
  ApprovalDetail,
  ApprovalList,
  AwardCheck,
  AwardView,
  CompletedChecks,
  DocumentList,
  QueueToday,
  StockAdminQueue,
  UploadDocumentResponse,
  type ApprovalDecision,
  type ApprovalHistory,
  type CompleteCheck,
  type CompleteChecks,
  type DocumentPurpose,
  type MoveApprovalPerson,
  type QueueViewId,
  type Resnapshot,
  type ReturnCheck,
  type SaveApprovalReview,
  type SetLineCostType,
  TaxEntryAward,
  TaxEntryList,
  type SaveTaxEntry,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// The operations screens' server calls: the Stock Admin queue and the one check (SA1, SA2), the
// approvals (SL7, X2, X3, R3) and award attachments. Every write returns the server's new view,
// whose `version` is the one to send next; a stale version answers 409 `conflict`.

/** Ids come from the address bar: only a UUID ever reaches a request path. */
const Id = z.uuid();

function path(id: string): string {
  return encodeURIComponent(Id.parse(id));
}

// --- Stock Admin queue and claims (SA1) -------------------------------------------------------------

export const queueKeys = {
  all: ['queue'] as const,
  view: (view: QueueViewId) => ['queue', view] as const,
};

/** One view of the Stock Admin queue (`GET /api/v1/queue?view=`), oldest first. */
export function queueQuery(view: QueueViewId) {
  return queryOptions({
    queryKey: queueKeys.view(view),
    queryFn: ({ signal }) =>
      request(`/api/v1/queue?view=${encodeURIComponent(view)}`, {
        schema: StockAdminQueue,
        signal,
      }),
    staleTime: 15_000,
  });
}

/** SA1's "Today" (`GET /api/v1/queue/today`): what happened on the queue today, newest first. */
export const queueTodayQuery = queryOptions({
  queryKey: [...queueKeys.all, 'today'] as const,
  queryFn: ({ signal }) => request('/api/v1/queue/today', { schema: QueueToday, signal }),
  staleTime: 30_000,
  refetchInterval: 60_000,
});

/** The award as its staff readers see it (`GET /api/v1/awards/:id`), for its current version. */
export function readAward(id: string, signal?: AbortSignal): Promise<AwardView> {
  return request(`/api/v1/awards/${path(id)}`, {
    schema: AwardView,
    ...(signal && { signal }),
  });
}

/** Claim an award: from the queue it starts the check; a check nobody holds is taken over. */
export function claimAward(id: string, version: number): Promise<AwardView> {
  return request(`/api/v1/awards/${path(id)}/claim`, {
    method: 'POST',
    body: { version },
    schema: AwardView,
  });
}

/** Hand a claim back (its holder), or release someone else's (a System Admin, with a reason). */
export function releaseClaim(id: string, reason?: string): Promise<AwardView> {
  return request(`/api/v1/awards/${path(id)}/claim/release`, {
    method: 'POST',
    body: reason === undefined ? {} : { reason },
    schema: AwardView,
  });
}

/** APP-271: take a teammate's claim over at Letters, with a reason. */
export function takeOverClaim(id: string, version: number, reason: string): Promise<AwardView> {
  return request(`/api/v1/awards/${path(id)}/claim/take-over`, {
    method: 'POST',
    body: { version, reason },
    schema: AwardView,
  });
}

// --- Tax to enter (APP-101) ----------------------------------------------------------------------

export const taxEntryKeys = {
  all: ['tax-entry'] as const,
  award: (id: string) => ['tax-entry', 'award', id] as const,
};

/** Every unreleased PAA's people with cash, those still to enter first (`GET /api/v1/tax-entry`). */
export const taxEntryQuery = queryOptions({
  queryKey: [...taxEntryKeys.all, 'list'] as const,
  queryFn: ({ signal }) => request('/api/v1/tax-entry', { schema: TaxEntryList, signal }),
  staleTime: 10_000,
});

/** One PAA's people and their tax, for the check and Letters (`GET /api/v1/awards/:id/tax`). */
export function awardTaxQuery(id: string) {
  return queryOptions({
    queryKey: taxEntryKeys.award(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/awards/${path(id)}/tax`, { schema: TaxEntryAward, signal }),
    staleTime: 10_000,
  });
}

/** Enter or change one person's taxes; a changed figure or gross answers 409 `tax_changed`. */
export function saveTaxEntry(lineId: string, body: SaveTaxEntry): Promise<TaxEntryAward> {
  return request(`/api/v1/tax-entry/lines/${path(lineId)}`, {
    method: 'PUT',
    body,
    schema: TaxEntryAward,
  });
}

// --- The one check (SA2) --------------------------------------------------------------------------

export const checkKeys = {
  one: (id: string) => ['check', id] as const,
};

export function checkQuery(id: string) {
  return queryOptions({
    queryKey: checkKeys.one(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/awards/${path(id)}/check`, { schema: AwardCheck, signal }),
    staleTime: 10_000,
  });
}

export function setCostType(id: string, body: SetLineCostType): Promise<AwardCheck> {
  return request(`/api/v1/awards/${path(id)}/check/cost-type`, {
    method: 'PUT',
    body,
    schema: AwardCheck,
  });
}

export function resnapshot(id: string, body: Resnapshot): Promise<AwardCheck> {
  return request(`/api/v1/awards/${path(id)}/check/resnapshot`, {
    method: 'POST',
    body,
    schema: AwardCheck,
  });
}

export function completeCheck(id: string, body: CompleteCheck): Promise<AwardView> {
  return request(`/api/v1/awards/${path(id)}/check/complete`, {
    method: 'POST',
    body,
    schema: AwardView,
  });
}

/**
 * Mark several awards checked from the queue (APP-105): each goes through, or stays with why. The
 * server takes up to 100 at a time, so a bigger selection goes in groups, one after another.
 */
export async function completeChecks(items: CompleteChecks['items']): Promise<CompletedChecks> {
  const result: CompletedChecks = { checked: [], left: [] };
  for (let i = 0; i < items.length; i += 100) {
    const part = await request('/api/v1/queue/checks', {
      method: 'POST',
      body: { items: items.slice(i, i + 100) },
      schema: CompletedChecks,
    });
    result.checked.push(...part.checked);
    result.left.push(...part.left);
  }
  return result;
}

/**
 * Send the award back to whoever entered it, with a note (APP-042): it becomes their draft again,
 * its hold and the claim end, and the check's estimates and switches are dropped.
 */
export function returnCheck(id: string, body: ReturnCheck): Promise<AwardView> {
  return request(`/api/v1/awards/${path(id)}/check/return`, {
    method: 'POST',
    body,
    schema: AwardView,
  });
}

// --- Approvals (SL7, X2, X3, R3) ----------------------------------------------------------------------

export const approvalKeys = {
  all: ['approvals'] as const,
  list: ['approvals', 'list'] as const,
  one: (id: string) => ['approvals', 'one', id] as const,
};

/** Following continuations past this many pages would mean something is wrong; stop there. */
const MAX_APPROVAL_PAGES = 40;

/**
 * Everything waiting on the person's decision (`GET /api/v1/approvals`), oldest first. The server
 * pages with an opaque cursor and may answer an empty page with a cursor, so this keeps
 * following it to the end. The first page also says whether each of the person's queues has ever
 * had work (`history`), so an empty page can tell its first day from "all caught up" (APP-070).
 */
export const approvalsQuery = queryOptions({
  queryKey: approvalKeys.list,
  queryFn: async ({
    signal,
  }): Promise<{ items: ApprovalDetail[]; history: ApprovalHistory | null }> => {
    const items: ApprovalDetail[] = [];
    let history: ApprovalHistory | null = null;
    let cursor: string | null = null;
    for (let page = 0; page < MAX_APPROVAL_PAGES; page += 1) {
      const query: string = cursor === null ? '' : `?cursor=${encodeURIComponent(cursor)}`;
      const answer: ApprovalList = await request(`/api/v1/approvals${query}`, {
        schema: ApprovalList,
        signal,
      });
      items.push(...answer.items);
      if (page === 0) history = answer.history ?? null;
      cursor = answer.nextCursor;
      if (cursor === null) break;
    }
    return { items, history };
  },
  staleTime: 15_000,
});

export function approvalQuery(id: string) {
  return queryOptions({
    queryKey: approvalKeys.one(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/approvals/${path(id)}`, { schema: ApprovalDetail, signal }),
    staleTime: 10_000,
  });
}

export function saveReview(id: string, body: SaveApprovalReview): Promise<ApprovalDetail> {
  return request(`/api/v1/approvals/${path(id)}/review`, {
    method: 'PUT',
    body,
    schema: ApprovalDetail,
  });
}

export function decide(id: string, body: ApprovalDecision): Promise<ApprovalDetail> {
  return request(`/api/v1/approvals/${path(id)}/decision`, {
    method: 'POST',
    body,
    schema: ApprovalDetail,
  });
}

export function movePerson(id: string, body: MoveApprovalPerson): Promise<ApprovalDetail> {
  return request(`/api/v1/approvals/${path(id)}/move-person`, {
    method: 'POST',
    body,
    schema: ApprovalDetail,
  });
}

// --- Attachments ------------------------------------------------------------------------------------

export const documentKeys = {
  one: (requestId: string) => ['documents', requestId] as const,
};

export function documentsQuery(requestId: string) {
  return queryOptions({
    queryKey: documentKeys.one(requestId),
    queryFn: ({ signal }) =>
      request(`/api/v1/awards/${path(requestId)}/documents`, { schema: DocumentList, signal }),
    staleTime: 10_000,
  });
}

export interface UploadInput {
  file: File;
  purpose: DocumentPurpose;
  lineId?: string;
  staffOnly?: boolean;
}

/** Attach a file (multipart). It stays unopenable until the virus scan clears it. */
export function uploadDocument(
  requestId: string,
  { file, purpose, lineId, staffOnly }: UploadInput,
): Promise<UploadDocumentResponse> {
  const form = new FormData();
  form.append('purpose', purpose);
  if (lineId !== undefined) form.append('lineId', Id.parse(lineId));
  if (staffOnly !== undefined) form.append('staffOnly', staffOnly ? 'true' : 'false');
  form.append('file', file, file.name);
  return request(`/api/v1/awards/${path(requestId)}/documents`, {
    method: 'POST',
    body: form,
    schema: UploadDocumentResponse,
  });
}

/** A new version of a file: it keeps the purpose, the person and who can see it. */
export function replaceDocument(
  requestId: string,
  documentId: string,
  file: File,
): Promise<UploadDocumentResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  return request(`/api/v1/awards/${path(requestId)}/documents/${path(documentId)}/replace`, {
    method: 'POST',
    body: form,
    schema: UploadDocumentResponse,
  });
}

/** Removes a file from the award (kept in its history, with the reason). */
export async function deleteDocument(
  requestId: string,
  documentId: string,
  reason: string,
): Promise<void> {
  await request(`/api/v1/awards/${path(requestId)}/documents/${path(documentId)}`, {
    method: 'DELETE',
    body: { reason },
    schema: z.undefined(),
  });
}

/** Where a file downloads from: a same-origin link the browser follows with the session cookie. */
export function documentHref(requestId: string, documentId: string): string {
  return `/api/v1/awards/${path(requestId)}/documents/${path(documentId)}/content`;
}
